import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, IconButton } from '@dopomogai/ui';
import { motion } from 'framer-motion';
import { Plus, Trash2, Edit, Calendar, SlidersHorizontal, Sparkles } from 'lucide-react';
import { SavedPresentation } from '@dopomogai/types';
import clsx from 'clsx';

interface PresentationListProps {
  presentations: SavedPresentation[];
  onAction: (action: 'load' | 'delete' | 'new', payload?: { id: string }) => void;
}

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric'
});

const getThemePreview = (p: SavedPresentation) => [
  p.theme.colors.primary, p.theme.colors.secondary, p.theme.colors.tertiary, p.theme.colors.quaternary
];


export const PresentationList: React.FC<PresentationListProps> = ({ presentations, onAction }) => {
  const { t } = useTranslation(['presentation_list', 'common']);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-slate-800">{t('title', 'My Presentations')}</h1>
          <Button variant="primary" onClick={() => onAction('new')} icon={<Plus size={18} />} text={t('common:new_presentation', 'New Presentation')} />
        </div>

        {presentations.length > 0 ? (
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
            variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.07 } } }}
            initial="hidden"
            animate="show"
          >
            {presentations.map((p) => (
              // The outer div is now only for layout and event handling.
              <div
                key={p.id}
                className="h-full cursor-pointer"
                onClick={() => onAction('load', { id: p.id })}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onAction('load', { id: p.id }); }}
              >
                {/* 
                  THE FIX: The Card component itself is now a motion component.
                  - motion.div has the Card's styles applied directly.
                  - whileHover applies transform and boxShadow to this single element.
                  - This guarantees the card and shadow animate perfectly together.
                */}
                <motion.div
                  variants={{ hidden: { y: 20, opacity: 0 }, show: { y: 0, opacity: 1 } }}
                  whileHover={{ y: -4, x: -4, boxShadow: '4px 4px 0px 0px rgb(17 24 39)' }}
                  whileTap={{ y: 0, x: 0, boxShadow: '1px 1px 0px 0px rgb(17 24 39)', scale: 0.99 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                  className="h-full flex flex-col bg-white border-2 border-black rounded-xl"
                >
                  <div className="p-5 flex flex-col flex-grow">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex -space-x-2">
                        {getThemePreview(p).map((color, i) => (
                          <div key={i} className="w-6 h-6 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: color }} />
                        ))}
                      </div>
                      {/* 
                        IconButton still uses group-hover from the parent div,
                        which is fine because it's a separate animation (opacity).
                      */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        whileHover={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                          <IconButton
                            icon={<Trash2 size={16} />}
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); onAction('delete', { id: p.id }); }}
                            tooltip={t('delete_tooltip', 'Delete')}
                            // FIX: Added a more descriptive ariaLabel
                            ariaLabel={`Delete presentation titled ${p.title}`}
                          />
                      </motion.div>
                    </div>
                    <div className="flex-grow">
                      <h2 className="text-lg font-bold text-slate-800 mb-2 line-clamp-2">{p.title}</h2>
                      <div className="flex items-center text-sm text-slate-500 mb-1">
                        <SlidersHorizontal size={14} className="mr-2" />
                        <span>{t('common:slide_count', { count: p.slideCount })}</span>
                      </div>
                      <div className="flex items-center text-sm text-slate-500">
                        <Calendar size={14} className="mr-2" />
                        <span>{t('updated_at', 'Updated:')} {formatDate(p.updatedAt)}</span>
                      </div>
                    </div>
                    <div className="mt-6 pt-4 border-t border-slate-200">
                      <Button variant="secondary" size="sm" fullWidth text={t('open_editor', 'Open Editor')} icon={<Edit size={14} />} />
                    </div>
                  </div>
                </motion.div>
              </div>
            ))}
          </motion.div>
        ) : (
          /* Empty State */
          <div className="text-center py-24 border-2 border-dashed border-slate-300 rounded-xl bg-white">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles size={32} />
            </div>
            <h2 className="text-2xl font-semibold text-slate-800 mb-2">{t('no_presentations_title', 'Create your first presentation')}</h2>
            <p className="text-slate-500 mb-6">{t('no_presentations_desc', 'Get started by creating a new presentation from scratch or with AI.')}</p>
            <Button variant="primary" size="lg" onClick={() => onAction('new')} icon={<Plus size={20} />} text={t('create_first', 'Create First Presentation')} />
          </div>
        )}
      </div>
    </div>
  );
};